import { useCallback, useEffect, useRef } from "react";
import { Feature, Point } from "geojson";
import { Tool } from "lib/types/Tool";
import { MarkerModel } from "components/elements/CesiumViewer/core/wrapper/GeographicMarkersPlugin";
import { useActiveTool } from "components/elements/CesiumViewer/core/tools/useActiveTool";
import { useCesium } from "components/elements/CesiumViewer/core/useCesium";

export const useGeographyMarkers = (
  features: Feature<Point>[],
  onClick: (id: number | string | undefined) => void,
  isVisible: boolean = true,
) => {
  const { activeTool } = useActiveTool();
  const { geographicMarkersPlugin } = useCesium();

  const featuresJson = JSON.stringify(features);

  //tolerate changing click handler without recreating the markers
  const onClickRef = useRef(onClick);
  useEffect(() => {
    onClickRef.current = onClick; //update outside of render
  }, [onClick]);

  const createMapMarkers = useCallback(() => {
    if (!features) return [];

    return features.map<MarkerModel>((feature) => {
      const { coordinates } = feature.geometry;
      const { name, markerScale, markerColor } = feature.properties;
      const onMarkerClick = () => {
        if (activeTool === Tool.none || activeTool === Tool.select)
          onClickRef.current?.(feature.id);
      };

      return {
        coordinates,
        markerScale,
        markerColor,
        tooltipText: name,
        onClick: onMarkerClick,
      };
    });
  }, [featuresJson, activeTool]);

  useEffect(() => {
    if (!features || !isVisible) return;

    const markers = createMapMarkers();
    geographicMarkersPlugin.setMarkers(markers);

    return () => {
      geographicMarkersPlugin.clearMarkers();
    };
  }, [featuresJson, isVisible, createMapMarkers]);
};
